import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { Phone, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function UserLogin() {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const { mobileLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSuccess = () => {
    toast.success("Successfully logged in!");
    const from = location.state?.from?.pathname || "/dashboard";
    navigate(from, { replace: true });
  };

  const handleMobileSubmit = async (e) => {
    e.preventDefault();
    if (!phone || phone.length < 10) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    setLoading(true);
    try {
      await mobileLogin(phone);
      handleSuccess();
    } catch (err) {
      toast.error(formatApiError(err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-[var(--bg)] px-5 py-12 relative overflow-hidden">
      {/* Background Decorative Blobs */}
      <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-[var(--primary)]/10 blur-[100px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40vw] h-[40vw] rounded-full bg-[#33CCFF]/10 blur-[100px] pointer-events-none" />

      <div className="w-full max-w-md bg-white rounded-3xl p-6 md:p-8 shadow-[8px_8px_0_0_#0A0A0A] border-[3px] border-black relative z-10">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[var(--primary)] border-[3px] border-black flex items-center justify-center font-display font-black text-white text-3xl shadow-[3px_3px_0_0_#0A0A0A] mx-auto mb-4">
            K
          </div>
          <h1 className="font-display font-black text-2xl uppercase tracking-tight">Login to Khasim</h1>
          <p className="text-gray-500 text-sm font-bold uppercase tracking-wider mt-1">Join the family</p>
        </div>

        {/* Mobile Form */}
        <form onSubmit={handleMobileSubmit} className="space-y-5">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Mobile Number</label>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter your mobile number"
                className="w-full pl-11 pr-4 py-3 bg-gray-50 border-[3px] border-black rounded-xl font-bold focus:bg-white focus:outline-none focus:shadow-[4px_4px_0_0_#0A0A0A] transition-all"
                disabled={loading}
              />
            </div>
            <p className="text-[11px] text-gray-500 font-medium mt-2">
              We'll securely log you in or register you instantly.
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-black text-white font-bold uppercase tracking-widest py-4 rounded-xl border-[3px] border-black hover:bg-[var(--primary)] hover:shadow-[4px_4px_0_0_#0A0A0A] transition-all disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="animate-spin" size={20} /> : "Continue"}
            {!loading && <ArrowRight size={20} />}
          </button>
        </form>
      </div>
    </div>
  );
}
